// LO QUE CUESTA UN SMS, DICHO EN UN SOLO SITIO.
//
// Pedido por Lissette (2026-09-20): que se vea que un SMS mandado desde Deskte
// CUESTA, a diferencia de escribirle al cliente desde el teléfono propio.
//
// ── POR QUÉ TAMBIÉN DICE LO QUE SE GANA ─────────────────────────────────────
//
// El 2026-09-01 Lissette pidió cerrar el camino gratis: *"se abre un programa
// afuera como mensajería de la Mac… ojo con eso"*. Un aviso que solo diga
// "cuesta dinero" empuja al equipo de vuelta a ese camino, que no deja rastro
// en la ficha, no respeta la lista de No-Textear y le enseña al cliente el
// número personal de quien esté sentado ahí. El aviso tiene que cerrar esa
// puerta, no reabrirla — por eso cada texto de aquí dice las dos mitades.
//
// ── POR QUÉ UN ARCHIVO Y NO UNA FRASE EN CADA PANTALLA ──────────────────────
//
// Porque la misma frase escrita en cinco sitios se separa, y lo que se
// separaría es un precio. Es el patrón que la casa ya usa dos veces:
// `greetings.js#borrowedEmailNotice` y `motivoDelSmsDeRenovacion.js`. Vive en
// un `.js` suelto para que una prueba pueda importarlo (regla de oro 48).
//
// ── EL PRECIO ───────────────────────────────────────────────────────────────
//
// Se reutiliza el ≈$0.007 que ya estaba escrito en Ajustes → SMS y Llamadas,
// dicho como "≈1 centavo" en el momento de mandar. NO se hace una tabla de
// precios por proveedor: una lista de proveedores escrita a mano ya mordió
// antes (E-127, regla de oro 81) y los precios cambian sin avisar.

/** Dólares por segmento de SMS. Un número, en un sitio. */
export const PRECIO_POR_SEGMENTO = 0.007;

/** El nombre del proveedor tal y como se le enseña a la gente. */
export function nombreDelProveedor(provider) {
  const p = String(provider || '').toUpperCase();
  return { TWILIO: 'Twilio', SIGNALWIRE: 'SignalWire', RINGCENTRAL: 'RingCentral', QUO: 'Quo', MIGHTYCALL: 'MightyCall' }[p]
    || (p ? p[0] + p.slice(1).toLowerCase() : '');
}

/**
 * La línea de al lado del botón de enviar, en las pantallas de uno en uno.
 *
 *   💬 Sale por tu línea de Quo · ≈1 centavo por mensaje
 */
export function lineaDeCostoSms(provider, lang) {
  const es = lang === 'es';
  const p = nombreDelProveedor(provider);
  const linea = p
    ? (es ? `Sale por tu línea de ${p}` : `Goes out through your ${p} line`)
    : (es ? 'Sale por tu línea conectada' : 'Goes out through your connected line');
  return `💬 ${linea} · ${es ? '≈1 centavo por mensaje' : '≈1 cent per message'}`;
}

/**
 * El cuadro que se enseña UNA vez, la primera que esa persona manda un SMS.
 *
 * La marca de "ya lo vio" va en `uiPrefs` como un booleano. Ojo: `uiPrefs`
 * tiene tope de tamaño y se poda (E-118) — una bandera, nunca el texto.
 */
export function cuadroDeCostoSms(provider, lang) {
  const es = lang === 'es';
  const p = nombreDelProveedor(provider) || (es ? 'tu proveedor' : 'your provider');
  return es
    ? `Los mensajes que salen de Deskte van por tu cuenta de ${p}, y te los cobra ${p} — no Deskte. A cambio quedan guardados en la ficha del cliente, respetan tu lista de No-Textear, y al cliente le llega el número de la agencia.`
    : `Messages sent from Deskte go through your ${p} account, and ${p} bills you for them — not Deskte. In exchange they stay on the client's record, they honor your Do-Not-Text list, and the client sees the agency's number.`;
}

/**
 * El párrafo de Ajustes → SMS y Llamadas. Vivía escrito a mano dentro de
 * `PhoneIntegrationView.jsx`; ahora sale de aquí, que es donde está el precio.
 */
export function costoAproximadoDeLaLinea(lang) {
  return lang === 'es'
    ? 'Costo aproximado (pagas al proveedor, no a Deskte): SMS ~$0.007 c/u, llamada ~$0.01/min, número ~$0.85–1.15/mes. Para textear como negocio en EE. UU. también se requiere el registro “A2P 10DLC” (~$4 una vez + ~$2/mes).'
    : 'Approximate cost (paid to the provider, not Deskte): SMS ~$0.007 each, calls ~$0.01/min, number ~$0.85–1.15/mo. Texting as a US business also requires “A2P 10DLC” registration (~$4 once + ~$2/mo).';
}

/**
 * En el ENVÍO MASIVO el aviso no es una línea, es el desglose con el total
 * ANTES de pulsar: cuántos mensajes, cuánto cuestan y cuánto tarda en salir.
 *
 * Los tres números juntos, porque los tres cambian la decisión y ninguno se
 * puede deducir de los otros. `minutos` sale de las 50 por minuto de la cola,
 * y es un suelo: ese lote se comparte con el correo.
 */
export function desgloseDelMasivo({ segmentos, destinatarios, minutos, lang }) {
  const es = lang === 'es';
  const total = Math.max(0, Number(segmentos) || 0) * Math.max(0, Number(destinatarios) || 0);
  const dolares = total * PRECIO_POR_SEGMENTO;
  const partes = [
    es
      ? `${destinatarios} destinatario${destinatarios === 1 ? '' : 's'} × ${segmentos} segmento${segmentos === 1 ? '' : 's'} = ${total} mensaje${total === 1 ? '' : 's'}`
      : `${destinatarios} recipient${destinatarios === 1 ? '' : 's'} × ${segmentos} segment${segmentos === 1 ? '' : 's'} = ${total} message${total === 1 ? '' : 's'}`,
    `≈ $${dolares.toFixed(2)} ${es ? 'que te cobra tu proveedor' : 'billed by your provider'}`,
  ];
  if (minutos > 1) {
    partes.push(es
      ? `salen a lo largo de unos ${minutos} minutos`
      : `they go out over about ${minutos} minutes`);
  }
  return partes.join(' · ');
}
